import type { CSSProperties } from 'react';
import { cx } from '@/lib/cx';

/*
 * The SEED EMERGE mark — an E whose top arm has come off and tilted. The letter
 * names the product, the detached arm is the verb.
 *
 * ONE COLOUR, DELIBERATELY, AND THAT IS LOAD-BEARING. An earlier direction set
 * part of the mark in `seed-navy` and it vanished against the BrandPanel, whose
 * field IS `#1a1f4e` — and no `dark:` variant or `prefers-color-scheme` branch
 * fixes that, because that navy is a brand SURFACE in the LIGHT theme too.
 * `seed-orange` alone clears white, seed-cream, seed-navy and the dark surface
 * with no variant and no new token, which is why this takes `currentColor` from
 * a single `text-seed-orange` default rather than branching on anything.
 *
 * DECORATIVE BY DEFAULT. Every mount in this app sets "SEED EMERGE" as real
 * text beside the mark, so the default is `aria-hidden` — a `title` here would
 * make a screen reader announce the product name twice. Pass `title` only where
 * the mark stands alone with no text beside it; nothing does today.
 *
 * ⚠️ THE viewBox IS TIGHTER THAN `public/brand/emerge-mark.svg` AND THE PATHS
 * ARE THE SAME. The standalone file keeps the full `0 0 64 64` box, whose
 * padding is what a favicon and a social card want; in a 28px sidebar chip that
 * same padding renders the mark at about 70% of the space and it reads as
 * undersized. Only the frame differs — if a path here ever disagrees with that
 * file, this one is the copy that drifted.
 */

/** Stem, middle arm and bottom arm, as ONE path so they cannot drift apart. */
const BODY = 'M14 25H23V33.5H43.5V41H23V45.5H49.5V53H14Z';
/** The lifted arm. The 10° rotation is baked in rather than left as a transform. */
const ARM = 'M22.35 13.12L47.95 8.6L49.26 15.99L23.65 20.5Z';

interface EmergeMarkProps {
  /** Sizing and spacing. The colour default is `text-seed-orange`; override it only for a genuinely one-colour context. */
  className?: string;
  /** For a size that is a computed number rather than a utility — a scale ladder, say. */
  style?: CSSProperties;
  /** Set ONLY when the mark stands alone. With text beside it, leave this undefined so the name is announced once. */
  title?: string;
}

export function EmergeMark({ className, style, title }: EmergeMarkProps) {
  return (
    <svg
      viewBox="5 4 54 54"
      className={cx('text-seed-orange', className)}
      style={style}
      fill="currentColor"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <path d={BODY} />
      <path d={ARM} />
    </svg>
  );
}
